'use client';

import Link from 'next/link';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import { CircleDollarSign, MessageCircle } from 'lucide-react';

import { formatCount } from '../../../functions/format-post-date';
import { usePostStats } from '../../hooks/usePostStats';
import { usePostUnlockActions } from '../../hooks/usePostUnlockActions';
import type { PostViewModel } from '../../types';
import { PostLikeButton } from './PostLikeButton';
import { PostShareButton } from './PostShareButton';
import { PostTimestamp } from './PostTimestamp';

interface Props {
  post: PostViewModel;
  sellerId: string;
  /** The surface's bio context (`null` on the non-1:1 DM inbox) — drives the
   *  Send-Tip checkout scope. */
  bioContext: BioContext | null;
  /**
   * Open comments in place instead of hard-navigating to the permalink. Supplied
   * by the creator wall (`/my-popstore`), where the permalink `<Link>` would
   * hard-nav out of the dashboard into the bio route group and 404. When set,
   * the comment count becomes a `<button>` that opens the shared post-detail
   * modal; when omitted (visitor feed / permalink page), it stays a `<Link>`.
   */
  onOpenComments?: () => void;
  /**
   * Rendered in the detail view (modal / permalink), where comments already show
   * alongside the post. The comment count is then INERT (a plain `<span>`) — no
   * link/button — so clicking it doesn't navigate/reopen the post onto itself.
   */
  detail?: boolean;
}

/**
 * Post footer — like + comment on the left, timestamp + Send Tip on the right.
 * Like + comment counts render from the post snapshot, then refresh from
 * {@link usePostStats} just before the footer scrolls into view (fresh public
 * counts + the viewer's `liked`); the like button toggles optimistically. The
 * comment count deep-links to the permalink's comments, and Send Tip launches a
 * one-shot tip to the post (`tip_type=post`, no goal) via the shared tip
 * launcher. Send Tip is hidden on crowd-tip posts, which route tipping through
 * the unlock CTA instead — 1:1 with legacy `PostFooter`.
 */
export function PostFooter({
  post,
  sellerId,
  bioContext,
  onOpenComments,
  detail = false,
}: Props) {
  const permalink = post.permalinkHref;
  // Send-Tip is hidden inside the CommentSold native app (App Store IAP).
  const isAppUser = useIsAppUser();
  const stats = usePostStats({
    storeId: sellerId,
    postId: post.id,
    snapshot: {
      liked: post.liked,
      likeCount: post.likeCount,
      commentCount: post.commentCount,
    },
  });
  const { onTip } = usePostUnlockActions({
    postId: post.id,
    urlCode: post.author.handle,
    sellerId,
    bioContext,
  });
  const showSendTip = post.gating.tip == null && !isAppUser;

  return (
    <div
      ref={stats.footerRef}
      className="flex items-center justify-between gap-3 pt-1"
    >
      <div className="flex items-center gap-4">
        <PostLikeButton
          liked={stats.liked}
          likeCount={stats.likeCount}
          onToggle={stats.toggleLike}
        />
        {post.commentsEnabled ? (
          // In the detail view comments already show alongside, so neither the
          // button nor the permalink link renders — the count falls through to
          // the inert `<span>`, avoiding a nav/reopen onto the same post.
          onOpenComments && !detail ? (
            <button
              type="button"
              onClick={onOpenComments}
              aria-label="View comments"
              className="flex items-center gap-1.5 text-[12px] opacity-80 transition hover:opacity-100"
            >
              <MessageCircle className="h-[18px] w-[18px]" aria-hidden />
              <span>{formatCount(stats.commentCount)}</span>
            </button>
          ) : permalink && !detail ? (
            <Link
              href={`${permalink}#comments`}
              aria-label="View comments"
              className="flex items-center gap-1.5 text-[12px] opacity-80 transition hover:opacity-100"
            >
              <MessageCircle className="h-[18px] w-[18px]" aria-hidden />
              <span>{formatCount(stats.commentCount)}</span>
            </Link>
          ) : (
            <span className="flex items-center gap-1.5 text-[12px] opacity-80">
              <MessageCircle className="h-[18px] w-[18px]" aria-hidden />
              <span>{formatCount(stats.commentCount)}</span>
            </span>
          )
        ) : null}
        {post.shareUrl ? (
          <PostShareButton url={post.shareUrl} title={post.title} />
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        {post.publishedAt != null ? (
          <PostTimestamp publishedAt={post.publishedAt} />
        ) : null}
        {showSendTip ? (
          <button
            type="button"
            onClick={onTip}
            /* TRANSLUCENT and FIXED, because the ground here is not a theme
               role at all — it is the creator's PUBLISHED post card, whose
               colour comes from their bio customization and can be anything.
               A 50% scrim adapts to it (grey on a white card, near-black on a
               dark one) and the pinned white ink reads on both: 3.98:1 and
               19.44:1.

               It was `bg-surface-2`, which is OPAQUE and, on this end-user
               surface, resolves on the visitor ramp to #222222 whatever the
               card is — a black blob on a white Fan Feed (QA). Dev had
               `bg-black/50 text-white`; `scrim` is that same fixed black as a
               role. Do not "theme" this: no role can track a colour the
               creator chose. */
            className="theme-keep bg-scrim/50 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold text-white"
          >
            <CircleDollarSign className="h-4 w-4 text-amber-400" aria-hidden />
            Send Tip
          </button>
        ) : null}
      </div>
    </div>
  );
}
